import { useState } from "react";

import { useHistory } from "react-router";

import { Button, classNames, Text } from "@alpacahq/ui/old";

import { OnboardingLayout } from "~/v2/components/onboarding";
import { Path } from "~/v2/path";
import { useLock } from "../../../../../api/hooks";
import { timer as entityOnboardingTimer } from "../../../entityOnboarding/timer";
import { logAmplitudeEvent as logEntityOnboardingAmplitudeEvent } from "../../../entityOnboarding/util";
import type { StepProps } from "../../session";
import { AlpacaXCryptoModal } from "./AlpacaXCryptoModal";
import { EntityAccountIcon, IndividualAccountIcon } from "./icons";
import {
  EntityLegalText,
  IndividualLegalText,
  NoSelectionLegalText,
} from "./legal";

type AccountType = "individual" | "entity";

export const Type = ({ session }: StepProps) => {
  const lock = useLock();
  const history = useHistory();

  const { account } = session;

  const isAlpacaXCrypto = account.crypto_broker === "BS";

  const [selected, setSelected] = useState<AccountType | null>(null);

  const onAccountTypeClicked = (accountType: AccountType | null) => () =>
    setSelected((prevAccountType) =>
      prevAccountType === accountType ? null : accountType,
    );

  const onContinueClick = async () => {
    if (lock.locked) {
      return;
    }

    lock.lock();

    if (selected === "entity") {
      entityOnboardingTimer.start();
      logEntityOnboardingAmplitudeEvent.onboardingStarted();
      history.push(Path.ROUTE_ENTITY_ONBOARDING_BUSINESS_INFO);
      lock.unlock();
      return;
    }

    lock.unlock();
    window.localStorage?.setItem("account_type_step", "true");
    session.next();
  };

  return (
    <OnboardingLayout title="Welcome to Alpaca!" percentage={0}>
      <div className="mt-2.5 flex flex-col justify-start gap-4 md:flex-row md:gap-16">
        <div className="md:min-w-56">
          <Text className="my-4 text-base">Select an account type</Text>

          <div className="flex w-full gap-4 md:max-w-56 md:flex-col">
            <Button
              variant="alternate"
              className={classNames(
                "flex w-full items-center justify-start border-2",
                selected === "individual" && "border-yellow-400",
              )}
              onClick={onAccountTypeClicked("individual")}
            >
              <IndividualAccountIcon />
              <span>Individual Account</span>
            </Button>

            <Button
              variant="alternate"
              className={classNames(
                "flex w-full items-center justify-start border-2",
                selected === "entity" && "border-yellow-400",
              )}
              onClick={onAccountTypeClicked("entity")}
            >
              <EntityAccountIcon />
              <span>Business Account</span>
            </Button>
          </div>
        </div>
        <div className="mt-4 min-h-[420px] w-full md:mt-12 md:mr-8">
          {selected === null && <NoSelectionLegalText />}
          {selected === "individual" && <IndividualLegalText />}
          {selected === "entity" && <EntityLegalText />}
        </div>
      </div>

      <div className="mt-6 flex justify-end md:mr-8">
        <Button onClick={onContinueClick} disabled={!selected}>
          Next
        </Button>
      </div>

      {isAlpacaXCrypto && <AlpacaXCryptoModal />}
    </OnboardingLayout>
  );
};

export default Type;
